import { Button, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/shared/components/DataTable";
import { AuditDetailDrawer } from "../components/AuditDetailDrawer";
import { AuditFilters } from "../components/AuditFilters";
import { useAuditList } from "../hooks/useAudit";
import type { AuditEntry, AuditFilterState } from "../types";
import { amalNomi } from "../utils/actions";

const { Title } = Typography;

/**
 * Audit jurnali — **faqat o'qish** (kontrakt §6.3).
 *
 * ⚠️ Yozish mexanizmi poydevorda: mutatsiya marshrutlari
 * `createAuditedRoute()` metadatasi bilan e'lon qilinadi va
 * `auditMiddleware` yozadi. Bu sahifa hech narsa yozmaydi — `GET /audit`
 * ning O'ZI ham audit yozmaydi, aks holda har sahifa ochilishi jurnalni
 * o'z-o'zidan to'ldirardi.
 */
export function AuditPage() {
	const { t } = useTranslation("audit");
	const [filters, setFilters] = useState<AuditFilterState>({});
	const [openEntryId, setOpenEntryId] = useState<string | null>(null);
	const query = useAuditList(filters);

	const columns: ColumnsType<AuditEntry> = [
		{
			title: t("columns.at"),
			key: "at",
			render: (_, row) => dayjs(row.at).format("DD.MM.YYYY HH:mm:ss"),
		},
		{
			title: t("columns.actor"),
			key: "actor",
			// ⚠️ `actor_user_id` — `set null`, va foydalanuvchining xodim
			// profili bo'lmasligi mumkin. Ikkala holatda ham xom `null`
			// o'rniga tushunarli belgi ko'rsatiladi.
			render: (_, row) => row.actorName ?? t("systemActor"),
		},
		{
			title: t("columns.action"),
			key: "action",
			// ⚠️ Noma'lum amal XOM qiymat bilan chiqadi (`amalNomi`).
			render: (_, row) => <Tag>{amalNomi(row.action, t)}</Tag>,
		},
		{ title: t("columns.resource"), dataIndex: "resourceType", key: "resourceType" },
		{
			title: t("columns.reason"),
			key: "reason",
			// ⚠️ Poydevor `setAuditReason` ni HECH BIR mavjud marshrutga
			// ulamagan (kontrakt §4.4) — ya'ni bu ustun faqat 4-bosqich
			// bloklarining yangi marshrutlarida to'ladi.
			render: (_, row) => row.reason ?? "—",
		},
		{
			title: "",
			key: "actions",
			render: (_, row) => (
				<Button type="link" onClick={() => setOpenEntryId(row.id)}>
					{t("page.detail")}
				</Button>
			),
		},
	];

	return (
		<div>
			<Title level={3} className="mb-6">
				{t("page.title")}
			</Title>

			<AuditFilters
				value={filters}
				onChange={(next) => {
					setFilters(next);
					query.setPage(1);
				}}
			/>

			<DataTable<AuditEntry> columns={columns} query={query} />

			<AuditDetailDrawer entryId={openEntryId} onClose={() => setOpenEntryId(null)} />
		</div>
	);
}
